import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { lightCssVars, darkCssVars } from '../themes/tokens';

export type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
  cssVars: Record<string, string>;
}

const STORAGE_KEY = 'theme-mode';

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    // Read initial value from localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
    // Default to light mode
    return 'light';
  });

  // Persist to localStorage when mode changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const setMode = (newMode: ThemeMode) => {
    setModeState(newMode);
  };

  const toggleMode = () => {
    setModeState((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const cssVars = useMemo(() => {
    return mode === 'light' ? lightCssVars : darkCssVars;
  }, [mode]);

  const value = useMemo(
    () => ({ mode, setMode, toggleMode, cssVars }),
    [mode, cssVars]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Default fallback for when context is not available (e.g., during hot reload)
const defaultContextValue: ThemeContextValue = {
  mode: 'light',
  setMode: () => {},
  toggleMode: () => {},
  cssVars: lightCssVars,
};

export const useThemeMode = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  // Return fallback instead of throwing to handle hot reload gracefully
  return context || defaultContextValue;
};
